import React from 'react';
import type { PhaseProgressionPreferences } from '@smartphone-tycoon/shared-core';

interface PhaseProgressionSettingsProps {
  preferences: PhaseProgressionPreferences;
  onPreferenceChange: (phase: keyof PhaseProgressionPreferences, choice: string) => void;
}

interface PhaseOption {
  phase: keyof PhaseProgressionPreferences;
  label: string;
  options: {
    value: string;
    label: string;
  }[];
}

const phaseOptions: PhaseOption[] = [
  {
    phase: 'design_complete',
    label: 'Design Phase Complete',
    options: [
      { value: 'proceed', label: 'Proceed to Next Stage' },
      { value: 'minor', label: 'Minor Investment' },
      { value: 'major', label: 'Major Investment' },
      { value: 'ask', label: 'Ask Every Time' }
    ]
  },
  {
    phase: 'engineering_complete',
    label: 'Engineering Phase Complete',
    options: [
      { value: 'proceed', label: 'Proceed to Next Stage' },
      { value: 'minor', label: 'Minor Investment' },
      { value: 'major', label: 'Major Investment' },
      { value: 'ask', label: 'Ask Every Time' }
    ]
  },
  {
    phase: 'testing_complete',
    label: 'Testing Phase Complete',
    options: [
      { value: 'proceed', label: 'Proceed to Next Stage' },
      { value: 'minor', label: 'Minor Investment' },
      { value: 'major', label: 'Major Investment' },
      { value: 'ask', label: 'Ask Every Time' }
    ]
  },
  {
    phase: 'manufacturing_complete',
    label: 'Manufacturing Setup Complete',
    options: [
      { value: 'proceed', label: 'Proceed to Next Stage' },
      { value: 'minor', label: 'Minor Investment' },
      { value: 'major', label: 'Major Investment' },
      { value: 'ask', label: 'Ask Every Time' }
    ]
  }
];

export function PhaseProgressionSettings({ preferences, onPreferenceChange }: PhaseProgressionSettingsProps) {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-theme-text-secondary border-b border-theme-border pb-2">
        Phase Progression Preferences
      </h3>
      
      <p className="text-xs text-theme-text-secondary">
        Choose your default action for each development phase. When set to a specific choice, 
        the game will automatically proceed with that option without prompting. 
        <strong>Investment choices automatically proceed after 3 applications.</strong>
      </p>
      
      <div className="mt-3 p-3 bg-theme-warning/10 rounded-lg">
        <p className="text-xs text-theme-warning">
          <strong>Smart Preferences:</strong> Products follow a priority system: 
          <strong>"Ask every time"</strong> and <strong>"Proceed"</strong> apply immediately. 
          Investment sequences (minor/major) finish naturally before applying new settings. 
          Check the product pipeline to see each product's current choice and progress.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-theme-border">
          <thead>
            <tr className="bg-theme-bg-secondary">
              <th className="px-3 py-2 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                Phase
              </th>
              {phaseOptions[0].options.map((option) => (
                <th key={option.value} className="px-3 py-2 text-center text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                  {option.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-theme-bg-content divide-y divide-theme-border">
            {phaseOptions.map((phase) => (
              <tr key={phase.phase}>
                <td className="px-3 py-3 text-sm font-medium text-theme-text-primary whitespace-nowrap">
                  {phase.label}
                </td>
                {phase.options.map((option) => (
                  <td key={option.value} className="px-3 py-3 text-center">
                    <input
                      type="radio"
                      name={phase.phase}
                      value={option.value}
                      checked={preferences[phase.phase] === option.value}
                      onChange={() => onPreferenceChange(phase.phase, option.value)}
                      className="h-4 w-4 text-theme-info border-theme-border focus:ring-theme-info"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 p-3 bg-theme-info/10 rounded-lg">
        <p className="text-xs text-theme-info">
          <strong>Tip:</strong> You can override these settings by unchecking "Don't ask me next time" 
          when a phase completion dialog appears. <br/>
          <strong>Tip:</strong> Select "Ask every time" to re-enable phase completion dialogs.
        </p>
      </div>
    </div>
  );
}